import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { SharedModule } from '../../../shared/shared.module';
import { AdministratorService } from '../../../services/administrator.service';
import { InventoryService } from '../../../services/inventory.service';
import { ProductLocation, Warehouse, WmsLocation } from '../../../model/administration.model';
import { gridPageRequest } from '../../../model/common/common.model';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-product-locations-lookup',
  standalone: true,
  imports: [SharedModule, CommonModule, FormsModule],
  providers: [MessageService, ConfirmationService],
  templateUrl: './product-locations-lookup.component.html',
  styleUrl: './product-locations-lookup.component.scss'
})
export class ProductLocationsLookupComponent implements OnInit {
  productId: number | null = null;
  productCode = '';
  productName = '';
  productDescription = '';
  uom = '';
  quantityOnHand: number | string = 0;
  qtyPicked: number | string = 0;

  warehouseList: Warehouse[] = [];
  selectedWarehouseId: number | null = null;
  locationOptions: WmsLocation[] = [];
  rows: ProductLocation[] = [];
  selectedRows: ProductLocation[] = [];
  deletedIds: number[] = [];
  saving = false;
  isEditMode = false;
  first = 0;
  rowsPerPage = 5;

  constructor(
    private config: DynamicDialogConfig,
    private ref: DynamicDialogRef,
    private locationService: AdministratorService,
    private inventoryService: InventoryService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) { }

  ngOnInit(): void {
    this.productId = Number(this.config.data?.ProductId) || null;
    this.productCode = this.config.data?.ProductCode || '';
    this.productName = this.config.data?.ProductName || '';
    this.productDescription = this.config.data?.ProductDescription || '';
    this.uom = this.resolveUomLabel(this.config.data?.UOM);
    this.rows = [this.createEmptyRow()];
    this.loadWarehouses();
    this.loadWmsLocations();
  }

  loadWarehouses() {
    const query: gridPageRequest = { pageIndex: 1, pageSize: 200, id: 0 };
    this.locationService.getAllWarehouse(query).subscribe({
      next: (data: any) => {
        this.warehouseList = data?.warehouses || [];
        if (this.warehouseList.length) {
          this.selectedWarehouseId = this.warehouseList[0].warehouseId;
          this.onWarehouseChange();
        }
      },
      error: err => console.error(err)
    });
  }

  loadWmsLocations() {
    const query: gridPageRequest = { pageIndex: 1, pageSize: 500, id: 0 };
    this.locationService.getWmsLocations(query).subscribe({
      next: (data: any) => {
        this.locationOptions = data?.locations || data?.Locations || [];
      },
      error: err => console.error(err)
    });
  }

  onWarehouseChange() {
    this.loadInventoryQty();
    this.loadProductLocations();
  }

  loadInventoryQty() {
    if (!this.productCode || !this.selectedWarehouseId) {
      this.quantityOnHand = this.formatQty(0);
      this.qtyPicked = this.formatQty(0);
      return;
    }

    this.inventoryService
      .getInventoryStatusByProductWarehouse(this.productCode, this.selectedWarehouseId)
      .subscribe({
        next: (data: any) => {
          if (data?.success === false) {
            this.quantityOnHand = this.formatQty(0);
            this.qtyPicked = this.formatQty(0);
            return;
          }
          const item = Array.isArray(data) ? data[0] : data;
          const qoh = Number(item?.quantityOnHand ?? item?.QuantityOnHand ?? 0);
          // Qty Picked comes from product ship qty (QuantityInShipped)
          const shipQty = Number(
            item?.quantityInShipped
            ?? item?.QuantityInShipped
            ?? item?.shipQty
            ?? item?.ShipQty
            ?? 0
          );
          this.quantityOnHand = this.formatQty(qoh);
          this.qtyPicked = this.formatQty(shipQty);
        },
        error: () => {
          this.quantityOnHand = this.formatQty(0);
          this.qtyPicked = this.formatQty(0);
        }
      });
  }

  loadProductLocations() {
    if (!this.productId || !this.selectedWarehouseId) {
      this.rows = [this.createEmptyRow()];
      this.resetPagination();
      return;
    }

    this.locationService.getProductLocations(this.productId, this.selectedWarehouseId).subscribe({
      next: (data: any) => {
        const list = Array.isArray(data)
          ? data
          : (data?.productLocations || data?.locations || data?.data || []);

        if (list?.length) {
          this.rows = list.map((item: any) => this.mapLocationRow(item));
          this.isEditMode = this.rows.some(r => !!r.productLocationId);
        } else {
          this.rows = [this.createEmptyRow()];
          this.isEditMode = false;
        }
        this.selectedRows = [];
        this.resetPagination();
      },
      error: () => {
        this.rows = [this.createEmptyRow()];
        this.isEditMode = false;
        this.resetPagination();
      }
    });
  }

  addRow() {
    this.rows = [...this.rows, this.createEmptyRow()];
    this.goToLastPage();
  }

  onPageChange(event: any) {
    this.first = Number(event?.first ?? 0);
    this.rowsPerPage = Number(event?.rows ?? this.rowsPerPage);
    this.ensureValidPage();
  }

  onLocationChange(row: ProductLocation) {
    const selected = this.locationOptions.find(l => l.wmsLocationId === row.wmsLocationId);
    row.locationCode = selected?.locationCode || '';
    row.locationDescription = selected?.locationName || '';
  }

  onPrimaryChange(row: ProductLocation) {
    if (row.isPrimary) {
      this.rows.forEach(r => {
        if (r !== row) {
          r.isPrimary = false;
        }
      });
    }
  }

  hasSelectedRows(): boolean {
    return !!this.selectedRows?.length;
  }

  deleteSelected() {
    if (!this.selectedRows?.length) return;

    this.confirmationService.confirm({
      header: 'Are you sure?',
      message: 'Delete selected location rows?',
      accept: () => {
        const selected = [...this.selectedRows];
        selected.forEach(row => {
          if (row.productLocationId) {
            this.deletedIds.push(row.productLocationId);
          }
        });
        this.rows = this.rows.filter(r => !selected.includes(r));
        this.selectedRows = [];
        if (!this.rows.length) {
          this.rows = [this.createEmptyRow()];
          this.resetPagination();
        } else {
          this.ensureValidPage();
        }

        if (this.deletedIds.length && this.productId) {
          this.locationService.deleteProductLocations({
            productId: this.productId,
            warehouseId: this.selectedWarehouseId,
            ids: this.deletedIds
          }).subscribe({
            next: () => {
              this.deletedIds = [];
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Selected locations deleted.'
              });
            },
            error: () => {
              this.messageService.add({
                severity: 'warn',
                summary: 'Warning',
                detail: 'Rows removed locally. Delete API not available yet.'
              });
            }
          });
        }
      }
    });
  }

  save() {
    if (!this.selectedWarehouseId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please select Warehouse.'
      });
      return;
    }

    const validRows = this.rows.filter(r => r.wmsLocationId != null);
    if (!validRows.length) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please add at least one location.'
      });
      return;
    }

    if (this.hasDuplicateLocations(validRows)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Same location cannot be selected more than once.'
      });
      return;
    }

    if (!this.productId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Product Id is missing.'
      });
      return;
    }

    const payload = {
      productId: this.productId,
      productCode: this.productCode,
      warehouseId: this.selectedWarehouseId,
      createdBy: localStorage.getItem('username'),
      updatedBy: localStorage.getItem('username'),
      deletedIds: this.deletedIds,
      locations: validRows.map(r => ({
        productLocationId: r.productLocationId || 0,
        productId: this.productId,
        productCode: this.productCode,
        warehouseId: this.selectedWarehouseId,
        wmsLocationId: r.wmsLocationId,
        locationCode: r.locationCode || '',
        locationDescription: r.locationDescription || '',
        uom: this.resolveUomLabel(r.uom || this.uom),
        isPrimary: !!r.isPrimary,
        currentQty: Number(r.currentQty || 0),
        pickQty: Number(r.pickQty || 0),
        newQty: Number(r.newQty || 0)
      }))
    };

    this.saving = true;
    this.locationService.saveProductLocations(payload)
      .pipe(finalize(() => this.saving = false))
      .subscribe({
        next: (res: any) => {
          if (res?.success === false) {
            this.messageService.add({
              severity: 'warn',
              summary: 'Warning',
              detail: res.message || 'Failed to save locations.'
            });
            return;
          }
          this.deletedIds = [];
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: res?.message || (this.isEditMode
              ? 'Product locations updated successfully.'
              : 'Product locations saved successfully.')
          });
          setTimeout(() => this.ref.close(true), 500);
        },
        error: err => {
          console.error(err);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: err?.error?.message || err?.error?.detail || 'Failed to save product locations.'
          });
        }
      });
  }

  private formatQty(value: number): string {
    return Number(value || 0).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  private createEmptyRow(): ProductLocation {
    return {
      productLocationId: undefined,
      productId: this.productId || undefined,
      warehouseId: this.selectedWarehouseId,
      wmsLocationId: null,
      locationCode: '',
      locationDescription: '',
      uom: this.uom,
      isPrimary: false,
      currentQty: 0,
      pickQty: 0,
      newQty: 0,
      tempId: this.createTempId()
    };
  }

  private mapLocationRow(item: any): ProductLocation {
    return {
      productLocationId: item.productLocationId ?? item.ProductLocationId,
      productId: item.productId ?? item.ProductId,
      warehouseId: item.warehouseId ?? item.WarehouseId ?? this.selectedWarehouseId,
      wmsLocationId: item.wmsLocationId ?? item.WmsLocationId ?? null,
      locationCode: item.locationCode ?? item.LocationCode,
      locationDescription: item.locationDescription ?? item.LocationDescription ?? item.locationName,
      uom: this.resolveUomLabel(item.uom ?? item.Uom ?? this.uom),
      isPrimary: item.isPrimary ?? item.IsPrimary ?? false,
      currentQty: item.currentQty ?? item.CurrentQty ?? 0,
      pickQty: item.pickQty ?? item.PickQty ?? item.maxQty ?? item.MaxQty ?? 0,
      newQty: item.newQty ?? item.NewQty ?? item.qty ?? item.Qty ?? 0,
      tempId: this.createTempId()
    };
  }

  private resolveUomLabel(value: any): string {
    if (!value) return '';
    if (typeof value === 'string' || typeof value === 'number') return String(value);
    return value.unitsofMeasure
      || value.unitsofMeasureCode
      || value.UnitsofMeasure
      || value.UnitsofMeasureCode
      || value.uom
      || '';
  }

  private hasDuplicateLocations(rows: ProductLocation[]): boolean {
    const ids = rows.map(r => r.wmsLocationId);
    return new Set(ids).size !== ids.length;
  }

  private createTempId(): string {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }

  private resetPagination() {
    this.first = 0;
  }

  private goToLastPage() {
    const pageSize = Math.max(1, this.rowsPerPage || 5);
    const total = this.rows.length;
    this.first = total <= 0
      ? 0
      : Math.floor((total - 1) / pageSize) * pageSize;
  }

  private ensureValidPage() {
    const pageSize = Math.max(1, this.rowsPerPage || 5);
    const total = this.rows.length;

    if (total <= 0) {
      this.first = 0;
      return;
    }

    const maxFirst = Math.floor((total - 1) / pageSize) * pageSize;
    if (this.first < 0 || this.first > maxFirst) {
      this.first = maxFirst;
    } else {
      this.first = Math.floor(this.first / pageSize) * pageSize;
    }
  }
}
